import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../../../../firebase-client';
import { getCurrencyMeta } from '../../../../../utils/currency';
import { formatEventPayoutTiming } from '../../../../../utils/eventPayoutTiming';
import PublicEventShareActions from '../PublicEventShareActions';
import WaitlistFields from './waitlistFields';
import EditDocumentsForm from '../../editDocuments';
import EmailComposerModal from '../../../../teams/components/modals/communication/emailComposer';
import OwnedBookingFormEditor from '../../bookingForms/OwnedBookingFormEditor';

const CODES = ['GBP', 'EUR', 'USD', 'JPY', 'AUD', 'CAD', 'NZD', 'CHF', 'SEK', 'NOK', 'DKK', 'KRW', 'VND'];

export default function TicketingSection({ teamId, form, onChange, commercialLocked = false, paymentBasisLocked = false }) {
  const [bookingV2Enabled, setBookingV2Enabled] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [linkedDocumentIds, setLinkedDocumentIds] = useState([]);
  const [documentSearch, setDocumentSearch] = useState('');
  const [showDocumentManager, setShowDocumentManager] = useState(false);
  const [showEventComposer, setShowEventComposer] = useState(false);
  const [documentRefresh, setDocumentRefresh] = useState(0);
  useEffect(() => {
    if (!teamId) return;
    getDoc(doc(db, 'teams', teamId))
      .then((snapshot) => setBookingV2Enabled(snapshot.data()?.modules?.events?.enabled === true && (snapshot.data()?.modules?.events?.bookingV2?.enabled === true || snapshot.data()?.modules?.events?.bookingV2Enabled === true)))
      .catch(() => setBookingV2Enabled(false));
  }, [teamId]);
  useEffect(() => {
    if (!teamId || !form.id) return;
    httpsCallable(
      functions,
      'listLinkableBookingDocuments',
    )({ teamId, sourceType: 'event' })
      .then((result) => {
        const rows = result.data?.documents || [];
        setDocuments(rows);
        setLinkedDocumentIds(rows.filter((document) => document.linkedEventIds?.includes(form.id)).map((document) => document.id));
      })
      .catch(() => {
        setDocuments([]);
        setLinkedDocumentIds([]);
      });
  }, [teamId, form.id, documentRefresh]);
  const setValue = (name, value) => onChange({ target: { name, value, type: 'custom' } });
  const advancedTickets = Array.isArray(form.ticketTypes) ? form.ticketTypes : [];
  const extras = Array.isArray(form.extras) ? form.extras : [];
  const addTicket = () =>
    setValue('ticketTypes', [
      ...advancedTickets,
      {
        id: crypto.randomUUID(),
        name: form.ticketName || 'General admission',
        priceMinor: Math.max(0, Math.round(Number(form.ticketPriceMajor || 0) * 100)),
        eligibility: 'everyone',
        capacity: '',
        memberTagNames: [],
        priceSchedule: [],
      },
    ]);
  const patchTicket = (index, patch) =>
    setValue(
      'ticketTypes',
      advancedTickets.map((ticket, i) => (i === index ? { ...ticket, ...patch } : ticket)),
    );
  const addExtra = () =>
    setValue('extras', [
      ...extras,
      {
        id: crypto.randomUUID(),
        name: '',
        priceMinor: 0,
        scope: 'attendee',
        capacity: '',
        maxQuantity: 1,
      },
    ]);
  const patchExtra = (index, patch) =>
    setValue(
      'extras',
      extras.map((extra, i) => (i === index ? { ...extra, ...patch } : extra)),
    );
  const { symbol, decimals } = getCurrencyMeta(form.ticketCurrency);
  const isRecurring = form.type === 'recurring';
  const perOccurrence = form.ticketPaymentBasis === 'per_occurrence';
  const payoutTiming = formatEventPayoutTiming(form, form.occurrenceId || '');
  const externalLocked = form.externalConfigurationLocked === true || paymentBasisLocked;
  const operationsSourceId = form.originalEventId || form.id;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        className="badge"
        style={{
          display: 'inline-block',
          padding: '6px 10px',
          borderRadius: 8,
          background: 'var(--accent-50, #e6f4ea)',
          border: '1px solid var(--accent-200, #b7e0c2)',
          fontSize: 12,
        }}
      >
        Ticketed event
      </div>

      {isRecurring && (
        <fieldset className="event-payment-basis" disabled={commercialLocked || paymentBasisLocked}>
          <legend className="field-label">How should members pay?</legend>
          <label className={`event-payment-basis__option ${perOccurrence ? 'is-selected' : ''}`}>
            <input type="radio" name="ticketPaymentBasis" value="per_occurrence" checked={perOccurrence} onChange={onChange} required />
            <span>
              <strong>Pay for each occurrence</strong>
              <small>Members choose and pay for each date separately.</small>
            </span>
          </label>
          <label className={`event-payment-basis__option ${form.ticketPaymentBasis === 'whole_series' ? 'is-selected' : ''}`}>
            <input type="radio" name="ticketPaymentBasis" value="whole_series" checked={form.ticketPaymentBasis === 'whole_series'} onChange={onChange} required />
            <span>
              <strong>Pay once for the whole series</strong>
              <small>One pass covers every remaining date through the series end.</small>
            </span>
          </label>
          {(commercialLocked || paymentBasisLocked) && <small className="text-muted">The payment basis is locked because tickets have been sold.</small>}
        </fieldset>
      )}

      <div className="form-grid">
        <div className="form-field">
          <span className="field-label">Capacity</span>
          <input id="capacity" name="capacity" type="number" min="1" value={form.capacity} onChange={onChange} className="input" required disabled={commercialLocked} />
        </div>

        <div className="form-field">
          <span className="field-label">Ticket name</span>
          <input id="ticketName" name="ticketName" value={form.ticketName} onChange={onChange} className="input" required disabled={commercialLocked} />
        </div>

        <div className="form-field">
          <span className="field-label">Currency</span>
          <select id="ticketCurrency" name="ticketCurrency" value={form.ticketCurrency} onChange={onChange} className="input" required>
            {CODES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <span className="field-label">
            Price ({form.ticketCurrency}
            {symbol ? `, ${symbol}` : ''})
          </span>
          <input id="ticketPriceMajor" name="ticketPriceMajor" type="text" inputMode="decimal" placeholder={decimals === 0 ? 'e.g. 1500' : `e.g. 15.${'0'.repeat(decimals)}`} value={form.ticketPriceMajor} onChange={onChange} className="input" required />
        </div>
      </div>
      <WaitlistFields form={form} onChange={onChange} />

      {operationsSourceId && (
        <button type="button" className="button button-secondary" onClick={() => setShowEventComposer(true)}>Email confirmed bookers</button>
      )}

      {form.id && (
        <div className="card" style={{ display: 'grid', gap: 8 }}>
          <strong>Event documents</strong>
          <p className="text-muted small">Link existing club documents or upload into the Events area. Removing an association does not delete the document.</p>
          <input className="input" placeholder="Search documents" value={documentSearch} onChange={(event) => setDocumentSearch(event.target.value)} />
          {documents
            .filter((document) => !documentSearch || `${document.title} ${document.category}`.toLowerCase().includes(documentSearch.toLowerCase()))
            .map((document) => (
              <label key={document.id} className="checkbox-row">
                <input type="checkbox" checked={linkedDocumentIds.includes(document.id)} onChange={(event) => setLinkedDocumentIds((ids) => (event.target.checked ? [...ids, document.id] : ids.filter((id) => id !== document.id)))} />
                <span>
                  {document.title}
                  <small>
                    {document.category ? `${document.category} · ` : ''}
                    {document.visibility.replace('_', ' ')}
                  </small>
                </span>
              </label>
            ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="button button-secondary"
              onClick={async () => {
                await httpsCallable(
                  functions,
                  'updateBookingDocumentLinks',
                )({
                  teamId,
                  sourceType: 'event',
                  sourceId: form.id,
                  documentIds: linkedDocumentIds,
                });
              }}
            >
              Save document links
            </button>
            <button type="button" className="button button-secondary" onClick={() => setShowDocumentManager(true)}>
              Upload or manage club documents
            </button>
          </div>
        </div>
      )}
      <EditDocumentsForm isOpen={showDocumentManager} teamId={teamId} defaultAssociation={form.id ? { sourceType: 'event', sourceId: form.id } : null} onClose={() => setShowDocumentManager(false)} onSave={() => setDocumentRefresh((value) => value + 1)} />
      <EmailComposerModal
        isOpen={showEventComposer}
        onClose={() => setShowEventComposer(false)}
        teamId={teamId}
        tags={[]}
        members={[]}
        defaultAudienceMode="booking"
        defaultBookingAudience={
          operationsSourceId
            ? {
                sourceType: 'event',
                sourceId: operationsSourceId,
                ...(form.occurrenceId ? { occurrenceId: form.occurrenceId } : {}),
              }
            : null
        }
        bookingAudienceOptions={
          operationsSourceId
            ? [
                {
                  label: form.occurrenceId ? `${form.name || 'Event'} — this occurrence` : `${form.name || 'Event'} — whole event or series`,
                  value: {
                    sourceType: 'event',
                    sourceId: operationsSourceId,
                    ...(form.occurrenceId ? { occurrenceId: form.occurrenceId } : {}),
                  },
                },
              ]
            : []
        }
      />

      {bookingV2Enabled && (
        <div className="card event-ticketing-v2">
          <div>
            <strong>Advanced ticket types and extras</strong>
            <div className="text-muted small">Leave this empty to keep the existing single-price ticket unchanged.</div>
          </div>
          {advancedTickets.map((ticket, index) => (
            <fieldset key={ticket.id} disabled={commercialLocked} className="form-grid event-ticketing-v2__item">
              <legend>{ticket.name || `Ticket ${index + 1}`}</legend>
              <label>
                Name
                <input className="input" value={ticket.name || ''} onChange={(event) => patchTicket(index, { name: event.target.value })} />
              </label>
              <label>
                Price ({form.ticketCurrency})
                <input
                  className="input"
                  inputMode="decimal"
                  value={(Number(ticket.priceMinor || 0) / 100).toFixed(2)}
                  onChange={(event) =>
                    patchTicket(index, {
                      priceMinor: Math.max(0, Math.round(Number(event.target.value || 0) * 100)),
                    })
                  }
                />
              </label>
              <label>
                Eligibility
                <select className="input" value={ticket.eligibility || 'everyone'} onChange={(event) => patchTicket(index, { eligibility: event.target.value })}>
                  <option value="everyone">Everyone</option>
                  <option value="members">Members</option>
                  <option value="non_members">Non-members</option>
                </select>
              </label>
              <label>
                Capacity (optional)
                <input className="input" type="number" min="1" value={ticket.capacity ?? ''} onChange={(event) => patchTicket(index, { capacity: event.target.value })} />
              </label>
              <label>Available from<input className="input" type="datetime-local" value={ticket.availableFrom?.slice(0, 16) || ''} onChange={(event) => patchTicket(index, { availableFrom: event.target.value })} /></label>
              <label>Available until<input className="input" type="datetime-local" value={ticket.availableUntil?.slice(0, 16) || ''} onChange={(event) => patchTicket(index, { availableUntil: event.target.value })} /></label>
              {ticket.eligibility === 'members' && <label>Eligible member tags (comma separated)<input className="input" value={(ticket.memberTagNames || []).join(', ')} onChange={(event) => patchTicket(index, { memberTagNames: event.target.value.split(',').map((value) => value.trim()).filter(Boolean) })} /></label>}
              <div className="event-ticketing-v2__schedule"><strong>Scheduled prices</strong>{(ticket.priceSchedule || []).map((entry, scheduleIndex) => <div key={scheduleIndex} className="event-ticketing-v2__schedule-row"><input aria-label="Price starts" className="input" type="datetime-local" value={entry.startsAt?.slice(0, 16) || ''} onChange={(event) => patchTicket(index, { priceSchedule: ticket.priceSchedule.map((row, rowIndex) => rowIndex === scheduleIndex ? { ...row, startsAt: event.target.value } : row) })} /><input aria-label="Scheduled price" className="input" inputMode="decimal" value={(Number(entry.priceMinor || 0) / 100).toFixed(2)} onChange={(event) => patchTicket(index, { priceSchedule: ticket.priceSchedule.map((row, rowIndex) => rowIndex === scheduleIndex ? { ...row, priceMinor: Math.max(0, Math.round(Number(event.target.value || 0) * 100)) } : row) })} /><button type="button" className="button button-danger" onClick={() => patchTicket(index, { priceSchedule: ticket.priceSchedule.filter((_, rowIndex) => rowIndex !== scheduleIndex) })}>Remove</button></div>)}<button type="button" className="button button-secondary event-ticketing-v2__inline-action" onClick={() => patchTicket(index, { priceSchedule: [...(ticket.priceSchedule || []), { startsAt: '', priceMinor: ticket.priceMinor }] })}>Add scheduled price</button></div>
              <button
                type="button"
                className="button button-danger event-ticketing-v2__remove"
                onClick={() =>
                  setValue(
                    'ticketTypes',
                    advancedTickets.filter((_, i) => i !== index),
                  )
                }
              >
                Remove
              </button>
            </fieldset>
          ))}
          <button type="button" className="button button-secondary event-ticketing-v2__add" disabled={commercialLocked} onClick={addTicket}>
            Add ticket type
          </button>
          {extras.map((extra, index) => (
            <fieldset key={extra.id} disabled={commercialLocked} className="form-grid event-ticketing-v2__item">
              <legend>{extra.name || `Extra ${index + 1}`}</legend>
              <label>
                Name
                <input className="input" value={extra.name || ''} onChange={(event) => patchExtra(index, { name: event.target.value })} />
              </label>
              <label>
                Price ({form.ticketCurrency})
                <input
                  className="input"
                  value={(Number(extra.priceMinor || 0) / 100).toFixed(2)}
                  onChange={(event) =>
                    patchExtra(index, {
                      priceMinor: Math.max(0, Math.round(Number(event.target.value || 0) * 100)),
                    })
                  }
                />
              </label>
              <label>
                Apply
                <select className="input" value={extra.scope || 'attendee'} onChange={(event) => patchExtra(index, { scope: event.target.value })}>
                  <option value="attendee">Per attendee</option>
                  <option value="booking">Per booking</option>
                </select>
              </label>
              <label>
                Capacity (optional)
                <input className="input" type="number" value={extra.capacity ?? ''} onChange={(event) => patchExtra(index, { capacity: event.target.value })} />
              </label>
              <label>Maximum quantity<input className="input" type="number" min="1" value={extra.maxQuantity || 1} onChange={(event) => patchExtra(index, { maxQuantity: event.target.value })} /></label>
              <button
                type="button"
                className="button button-danger event-ticketing-v2__remove"
                onClick={() =>
                  setValue(
                    'extras',
                    extras.filter((_, i) => i !== index),
                  )
                }
              >
                Remove
              </button>
            </fieldset>
          ))}
          <button type="button" className="button button-secondary event-ticketing-v2__add" disabled={commercialLocked} onClick={addExtra}>
            Add paid extra
          </button>
        </div>
      )}

      {bookingV2Enabled && (
        <div style={{ display: 'grid', gap: 12 }}>
          <OwnedBookingFormEditor teamId={teamId} ownerType="event" ownerId={form.originalEventId || form.id || ''} ownerName={form.name || 'Event'} bookingForm={form.bookingForm} onPublished={(reference) => setValue('bookingForm', reference)} />
          <label>
            Protected answer retention (days)
            <input className="input" type="number" min="1" max="730" value={form.protectedAnswerRetentionDays || 30} onChange={(event) => setValue('protectedAnswerRetentionDays', event.target.value)} />
          </label>
        </div>
      )}

      <div className="checkbox-row">
        <input type="checkbox" id="admissionTicketsEnabled" name="admissionTicketsEnabled" checked={form.admissionTicketsEnabled === true} onChange={onChange} disabled={form.admissionTicketingLocked === true} />
        <label htmlFor="admissionTicketsEnabled">
          Issue scannable admission tickets
          <small className="text-muted"> One QR pass is created for each paid place.</small>
        </label>
      </div>
      {form.admissionTicketingLocked === true && <small className="text-muted">Scannable tickets are locked on because passes have already been issued.</small>}

      {isRecurring && perOccurrence && (
        <div className="form-grid">
          <div className="form-field">
            <span className="field-label">Sales open (days before each date)</span>
            <input id="ticketSaleOpenDaysBefore" name="ticketSaleOpenDaysBefore" type="number" min="0" step="1" value={form.ticketSaleOpenDaysBefore} onChange={onChange} className="input" required disabled={commercialLocked} />
          </div>
          <div className="form-field">
            <span className="field-label">Sales close (hours before each date)</span>
            <input id="ticketSaleCloseHoursBefore" name="ticketSaleCloseHoursBefore" type="number" min="0" step="0.5" value={form.ticketSaleCloseHoursBefore} onChange={onChange} className="input" required disabled={commercialLocked} />
          </div>
          <small className="text-muted">This window repeats for every occurrence.</small>
        </div>
      )}

      <div className="checkbox-row">
        <input type="checkbox" id="allowNonMembers" name="allowNonMembers" checked={!!form.allowNonMembers} onChange={onChange} disabled={commercialLocked} />
        <label htmlFor="allowNonMembers">Allow non-members to buy tickets</label>
      </div>

      {form.allowNonMembers && (
        <div className="external-event-settings">
          <div className="external-event-settings__header">
            <div>
              <strong>External attendee settings</strong>
              <div className="text-muted small">Choose how public bookings identify everyone attending.</div>
            </div>
            {externalLocked && <span className="badge">Locked after first sale</span>}
          </div>

          <fieldset disabled={commercialLocked || externalLocked} className="external-event-settings__choices">
            <legend className="field-label">Who needs a Member Nest account?</legend>
            <label>
              <input type="radio" name="externalAttendeeMode" value="individual_accounts" checked={form.externalAttendeeMode === 'individual_accounts'} onChange={onChange} />
              <span>
                <strong>Every attendee uses their own account</strong>
                <small>Each person purchases one ticket and maintains their own details.</small>
              </span>
            </label>
            <label>
              <input type="radio" name="externalAttendeeMode" value="buyer_managed_guests" checked={form.externalAttendeeMode === 'buyer_managed_guests'} onChange={onChange} />
              <span>
                <strong>Buyer can register named guests</strong>
                <small>One account can purchase for a named group and manage their details.</small>
              </span>
            </label>
          </fieldset>

          <fieldset disabled={commercialLocked || externalLocked} className="external-event-settings__choices">
            <legend className="field-label">Information required for each attendee</legend>
            <label>
              <input type="radio" name="externalInfoPreset" value="basic" checked={form.externalInfoPreset === 'basic'} onChange={onChange} />
              <span>
                <strong>Basic</strong>
                <small>Name and contact phone.</small>
              </span>
            </label>
            <label>
              <input type="radio" name="externalInfoPreset" value="safety" checked={form.externalInfoPreset === 'safety'} onChange={onChange} />
              <span>
                <strong>Safety</strong>
                <small>Name, date of birth, phone and an emergency contact.</small>
              </span>
            </label>
          </fieldset>

          <label className="form-field">
            <span className="field-label">Public description (optional)</span>
            <textarea className="input" name="publicDescription" rows={4} maxLength={2000} value={form.publicDescription || ''} onChange={onChange} disabled={commercialLocked} placeholder="Tell external visitors what the event involves and what they should bring." />
          </label>

          <div className="external-event-settings__location">
            <strong>Public location preview:</strong> {form.eventMode === 'online' ? 'Online' : [form.locationCity, form.locationCountry].filter(Boolean).join(', ') || 'Add a city and country'}
            <small> The exact address or online joining link is only shown after payment.</small>
          </div>

          {form.publicEventsSlug && form.publicSlug ? <PublicEventShareActions form={form} isAdmin compact /> : <p className="text-muted small">The public link will appear as soon as you save this event.</p>}
        </div>
      )}

      {!isRecurring && (
        <div className="checkbox-row">
          <input type="checkbox" id="controlSalesTime" name="controlSalesTime" checked={!!form.controlSalesTime} onChange={onChange} />
          <label htmlFor="controlSalesTime">Control sales time window</label>
        </div>
      )}

      {!isRecurring && form.controlSalesTime && (
        <div className="form-grid">
          <div className="form-field">
            <span className="field-label">Sales start</span>
            <input id="ticketSaleStart" name="ticketSaleStart" type="datetime-local" value={form.ticketSaleStart} onChange={onChange} className="input" required />
          </div>
          <div className="form-field">
            <span className="field-label">Sales end</span>
            <input id="ticketSaleEnd" name="ticketSaleEnd" type="datetime-local" value={form.ticketSaleEnd} onChange={onChange} className="input" required />
          </div>
        </div>
      )}

      <div className="form-grid">
        <div className="form-field">
          <span className="field-label">Booking fee handling</span>
          <select id="ticketPlatformFeeHandling" name="ticketPlatformFeeHandling" value={form.ticketPlatformFeeHandling} onChange={onChange} className="input">
            <option value="add">Add on top</option>
            <option value="include">Incorporate in price</option>
          </select>
        </div>
      </div>

      {isRecurring && form.checkoutUrl && <div className="error-message">Static checkout links cannot be used for recurring paid events.</div>}

      {(form.stripePriceId || (!isRecurring && form.checkoutUrl)) && (
        <div className="small-muted">
          Stripe price: {form.stripePriceId || '—'}
          <br />
          Checkout:{' '}
          {form.checkoutUrl ? (
            <a href={form.checkoutUrl} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            '—'
          )}
        </div>
      )}

      {payoutTiming && (
        <div className="event-payout-timing" role="note">
          <strong>When the club gets paid</strong>
          <span>{payoutTiming}</span>
        </div>
      )}
    </div>
  );
}
